import React, { useEffect, useEffectEvent } from "react";

import { CountriesLangFactory } from "@bokio/lang";
import { getCountryLabel } from "@bokio/shared/utils/companyInfoUtils";

import {
	hasInternationalPrefix,
	parsePhoneNumberWithCountryCode,
	stripLeadingNationalDigits,
} from "./phoneNumberUtils";

import type { SelectOption } from "../SearchableSelect/SearchableSelectOption";
import type { PhoneCountry } from "./PhoneCountry";

interface UsePhoneInputFieldParams {
	value: string | undefined;
	defaultIsoCountryCode2: string;
	countries: PhoneCountry[];
	onChange?: (value: string) => void;
	onSelectedCountryChange?: (countryIsoCode2: string, phoneCountryCode: string) => void;
	disableFormatting: boolean;
}

export const usePhoneInputField = ({
	value,
	defaultIsoCountryCode2,
	countries,
	onChange,
	onSelectedCountryChange,
	disableFormatting,
}: UsePhoneInputFieldParams) => {
	const [selectedCountry, setSelectedCountry] = React.useState<string>(() => {
		if (!disableFormatting && value) {
			const parsed = parsePhoneNumberWithCountryCode(value, countries);
			if (parsed.isoCountryCode2) {
				return parsed.isoCountryCode2;
			}
			// Value exists but has no international prefix â€” don't guess the country.
			// The user must explicitly select one before saving.
			return "";
		}
		// No value (new entry) â€” use the provided default.
		return defaultIsoCountryCode2;
	});

	const phoneInputId = `phone-input-${React.useId()}`;

	const countriesLang = CountriesLangFactory();

	const selectOptions: SelectOption<string>[] = React.useMemo(() => {
		return (
			countries
				.filter(country => country.InternationalCallingCode != null)
				.map(country => ({
					key: country.IsoCode2,
					value: country.IsoCode2,
					label: `${getCountryLabel(country.IsoCode3, countriesLang)} +${country.InternationalCallingCode}`,
				}))
				// Sort alphabetically, but pin the default country to the top of the list
				.sort((a, b) => {
					if (a.value === defaultIsoCountryCode2) {
						return -1;
					}
					if (b.value === defaultIsoCountryCode2) {
						return 1;
					}
					return a.label.localeCompare(b.label);
				})
		);
	}, [countries, countriesLang, defaultIsoCountryCode2]);

	const searchCountry = React.useCallback(
		(option: SelectOption<string>, term: string) => {
			const country = countries.find(c => c.IsoCode2 === option.value);
			const callingCode = country?.InternationalCallingCode ?? "";
			const normalizedTerm = term.replace(/^\+/, "").toLowerCase();
			return (
				option.label.toLowerCase().includes(normalizedTerm) ||
				callingCode.startsWith(normalizedTerm) ||
				option.value.toLowerCase().includes(normalizedTerm)
			);
		},
		[countries],
	);

	const getSelectedCountryFormatted = (): string => {
		if (!selectedCountry) {
			return "--";
		}
		const selectedCountryData = countries.find(country => country.IsoCode2 === selectedCountry);
		return `${selectedCountryData?.IsoCode2 || ""} +${selectedCountryData?.InternationalCallingCode || ""}`;
	};

	const handlePhoneChange = React.useCallback(
		(newValue: string) => {
			if (disableFormatting) {
				onChange?.(newValue);
				return;
			}

			// First, try to parse the input for country code
			const parsed = parsePhoneNumberWithCountryCode(newValue, countries);

			// If we found a country code, update the country (even if it's the same)
			if (parsed.isoCountryCode2) {
				setSelectedCountry(parsed.isoCountryCode2);
				const selectedCountryData = countries.find(country => country.IsoCode2 === parsed.isoCountryCode2);
				onSelectedCountryChange?.(parsed.isoCountryCode2, selectedCountryData?.InternationalCallingCode || "");
			}

			// Use the national number (without country code) â€” stripping of leading national digits is deferred to blur
			onChange?.(parsed.nationalNumber);
		},
		[countries, disableFormatting, onChange, onSelectedCountryChange],
	);

	const handlePhoneBlur = React.useCallback(() => {
		if (disableFormatting || !value) {
			return;
		}

		const shouldStrip = !hasInternationalPrefix(value);
		if (shouldStrip) {
			const strippedValue = stripLeadingNationalDigits(value, selectedCountry);
			if (strippedValue !== value) {
				onChange?.(strippedValue);
			}
		}
	}, [disableFormatting, onChange, selectedCountry, value]);

	const handleCountryChange = React.useCallback(
		(option: SelectOption<string> | undefined) => {
			if (option) {
				const selectedCountryData = countries.find(country => country.IsoCode2 === option.value);
				setSelectedCountry(option.value);
				onSelectedCountryChange?.(option.value, selectedCountryData?.InternationalCallingCode || "");

				// Apply stripping to current phone number when country changes (unless formatting is disabled)
				if (!disableFormatting && value) {
					const strippedValue = stripLeadingNationalDigits(value, option.value);
					if (strippedValue !== value) {
						onChange?.(strippedValue);
					}
				}
			}
		},
		[countries, disableFormatting, onChange, onSelectedCountryChange, value],
	);

	// On mount: format initial value and always notify parent of the resolved country code.
	const formatAndNotifyParent = useEffectEvent(() => {
		if (value && !disableFormatting) {
			const parsed = parsePhoneNumberWithCountryCode(value, countries);

			if (parsed.isoCountryCode2) {
				// Country detected from value â€” strip and notify with resolved country.
				onChange?.(stripLeadingNationalDigits(parsed.nationalNumber, parsed.isoCountryCode2));
				const countryData = countries.find(c => c.IsoCode2 === parsed.isoCountryCode2);
				onSelectedCountryChange?.(parsed.isoCountryCode2, countryData?.InternationalCallingCode || "");
			} else {
				// No country detected (legacy number without + prefix) â€” pass value as-is
				// and notify with empty country so the parent can require explicit selection.
				onChange?.(parsed.nationalNumber);
				onSelectedCountryChange?.("", "");
			}
		} else {
			// No value, or formatting disabled â€” notify with default country.
			if (value) {
				onChange?.(value);
			}
			const countryData = countries.find(c => c.IsoCode2 === defaultIsoCountryCode2);
			if (countryData) {
				onSelectedCountryChange?.(defaultIsoCountryCode2, countryData.InternationalCallingCode || "");
			}
		}
	});

	useEffect(() => {
		formatAndNotifyParent();
	}, []);

	return {
		selectedCountry,
		phoneInputId,
		selectOptions,
		searchCountry,
		getSelectedCountryFormatted,
		handlePhoneChange,
		handlePhoneBlur,
		handleCountryChange,
	};
};
